import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";

const PHASE_DETAIL_SCROLL_SPEED_PX_PER_MS = 0.02;
const PHASE_DETAIL_SCROLL_START_DELAY_MS = 3000;
const ESTIMATED_CHARS_PER_LINE = 72;

const estimateLineCount = (value) => {
  const text = String(value || "").trim();
  if (!text) return 1;

  const explicitLineCount = text.split(/\n/).filter(Boolean).length || 1;
  const estimatedWrappedLineCount = Math.ceil(text.length / ESTIMATED_CHARS_PER_LINE);
  return Math.max(explicitLineCount, estimatedWrappedLineCount, 1);
};

const measuredLineCountFor = (container) => {
  const textNode = container?.firstElementChild;
  if (!textNode || typeof window === "undefined") return null;

  const computedStyle = window.getComputedStyle(textNode);
  const resolvedLineHeight = Number.parseFloat(computedStyle.lineHeight);
  const textHeight = textNode.getBoundingClientRect?.().height || textNode.scrollHeight || 0;
  if (!Number.isFinite(resolvedLineHeight) || resolvedLineHeight <= 0 || textHeight <= 0) {
    return null;
  }

  return Math.max(1, Math.round(textHeight / resolvedLineHeight));
};

const LoadingPhaseReadableText = ({
  color,
  dataRole = "description",
  fontSize,
  fontWeight = 650,
  hasEvidenceItems,
  leftAlignMinLines = 1,
  lineHeight = 1.28,
  maxHeight,
  text,
  textSx = {},
  phaseMeta,
  reduceMotion,
  whiteSpace = "normal",
  windowSx = {},
}) => {
  const viewportRef = useRef(null);
  const [displayText, setDisplayText] = useState(() => String(text || ""));
  const [hasOverflow, setHasOverflow] = useState(false);
  const [lineCount, setLineCount] = useState(() => estimateLineCount(text));
  const [showTopFade, setShowTopFade] = useState(false);
  const displayTextRef = useRef(displayText);
  const pendingTextRef = useRef(null);
  const hasOverflowRef = useRef(false);
  const scrollCompleteRef = useRef(true);
  const scrollState = hasOverflow ? (reduceMotion ? "manual" : "auto-once") : "none";
  const shouldLeftAlign = lineCount >= leftAlignMinLines;
  const topFadeMask = showTopFade
    ? "linear-gradient(to bottom, transparent 0px, #000 18px, #000 calc(100% - 10px), transparent 100%)"
    : "linear-gradient(to bottom, #000 0px, #000 calc(100% - 10px), transparent 100%)";
  const windowDataAttrs = {
    [`data-loading-phase-${dataRole}-window`]: "true",
    [`data-loading-phase-${dataRole}-scroll`]: scrollState,
    [`data-loading-phase-${dataRole}-fade`]: "top-on-scroll",
    [`data-loading-phase-${dataRole}-align`]: shouldLeftAlign ? "left" : "center",
  };

  const applyDisplayText = useCallback((nextText) => {
    displayTextRef.current = nextText;
    scrollCompleteRef.current = false;
    setLineCount(estimateLineCount(nextText));
    setDisplayText(nextText);
  }, []);

  const flushPendingText = useCallback(() => {
    const pendingText = pendingTextRef.current;
    if (pendingText == null || pendingText === displayTextRef.current) {
      pendingTextRef.current = null;
      return;
    }

    pendingTextRef.current = null;
    applyDisplayText(pendingText);
  }, [applyDisplayText]);

  const markScrollComplete = useCallback(() => {
    scrollCompleteRef.current = true;
    flushPendingText();
  }, [flushPendingText]);

  useEffect(() => {
    displayTextRef.current = displayText;
  }, [displayText]);

  useEffect(() => {
    const nextText = String(text || "");
    if (nextText === displayTextRef.current) {
      pendingTextRef.current = null;
      return;
    }

    if (hasOverflowRef.current && !scrollCompleteRef.current && !reduceMotion) {
      pendingTextRef.current = nextText;
      return;
    }

    pendingTextRef.current = null;
    applyDisplayText(nextText);
  }, [applyDisplayText, reduceMotion, text]);

  useEffect(() => {
    const node = viewportRef.current;
    if (!node) return undefined;

    const updateOverflow = () => {
      const nextHasOverflow = node.scrollHeight > node.clientHeight + 1;
      const nextLineCount = measuredLineCountFor(node) || estimateLineCount(displayText);

      setLineCount((previousLineCount) => (
        previousLineCount === nextLineCount ? previousLineCount : nextLineCount
      ));
      hasOverflowRef.current = nextHasOverflow;
      setHasOverflow(nextHasOverflow);
      setShowTopFade(nextHasOverflow && node.scrollTop > 1);
      if (!nextHasOverflow || reduceMotion) {
        scrollCompleteRef.current = true;
        if (!nextHasOverflow) node.scrollTop = 0;
        flushPendingText();
      } else {
        scrollCompleteRef.current = false;
      }
    };

    updateOverflow();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateOverflow);
      observer.observe(node);
      if (node.firstElementChild) observer.observe(node.firstElementChild);
      return () => observer.disconnect();
    }

    if (typeof window !== "undefined") {
      window.addEventListener("resize", updateOverflow);
      return () => window.removeEventListener("resize", updateOverflow);
    }

    return undefined;
  }, [displayText, flushPendingText, maxHeight, reduceMotion]);

  useEffect(() => {
    const node = viewportRef.current;
    if (!node || reduceMotion || !hasOverflow || typeof window === "undefined") {
      if (node) node.scrollTop = 0;
      return undefined;
    }

    let intervalId;
    const pauseUntil = Date.now() + PHASE_DETAIL_SCROLL_START_DELAY_MS;

    const stepScroll = () => {
      const maxScroll = Math.max(node.scrollHeight - node.clientHeight, 0);
      if (maxScroll <= 1) {
        node.scrollTop = 0;
        setShowTopFade(false);
        markScrollComplete();
        return;
      }

      if (Date.now() < pauseUntil) return;

      const nextScrollTop = Math.min(
        maxScroll,
        node.scrollTop + PHASE_DETAIL_SCROLL_SPEED_PX_PER_MS * 50,
      );

      node.scrollTop = nextScrollTop;
      setShowTopFade(nextScrollTop > 1);
      if (nextScrollTop >= maxScroll) {
        window.clearInterval(intervalId);
        markScrollComplete();
      }
    };

    node.scrollTop = 0;
    setShowTopFade(false);
    scrollCompleteRef.current = false;
    intervalId = window.setInterval(stepScroll, 50);

    return () => window.clearInterval(intervalId);
  }, [displayText, hasOverflow, markScrollComplete, reduceMotion]);

  const handleScroll = useCallback(() => {
    const node = viewportRef.current;
    if (!node || !hasOverflowRef.current) return;

    const maxScroll = Math.max(node.scrollHeight - node.clientHeight, 0);
    setShowTopFade(node.scrollTop > 1);
    if (maxScroll <= 1 || node.scrollTop >= maxScroll - 1) {
      markScrollComplete();
    }
  }, [markScrollComplete]);

  return (
    <Box
      ref={viewportRef}
      onScroll={handleScroll}
      {...windowDataAttrs}
      sx={{
        mt: hasEvidenceItems ? 0.7 : 0.85,
        maxWidth: "100%",
        width: "100%",
        maxHeight,
        overflowY: hasOverflow ? "auto" : "visible",
        overscrollBehavior: "contain",
        pointerEvents: hasOverflow ? "auto" : "none",
        scrollbarWidth: hasOverflow ? "thin" : "none",
        scrollbarColor: hasOverflow ? `${phaseMeta.panelBorder} transparent` : "auto",
        ...(hasOverflow ? {
          maskImage: topFadeMask,
          WebkitMaskImage: topFadeMask,
        } : {}),
        "&::-webkit-scrollbar": {
          width: hasOverflow ? 4 : 0,
        },
        "&::-webkit-scrollbar-thumb": {
          borderRadius: 999,
          backgroundColor: phaseMeta.panelBorder,
        },
        ...windowSx,
      }}
    >
      <Typography
        component="p"
        sx={{
          m: 0,
          fontSize,
          lineHeight,
          color: color || phaseMeta.bodyColor,
          fontWeight,
          letterSpacing: 0,
          textAlign: shouldLeftAlign ? "left" : "center",
          whiteSpace,
          overflowWrap: "anywhere",
          ...textSx,
        }}
      >
        {displayText}
      </Typography>
    </Box>
  );
};

export default LoadingPhaseReadableText;
